import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';
import DashBoardDonutChart from "../admin/components/DashBoardDonutChart";
import DashBoardBarChart from "../admin/components/DashBoardBarChart";
import DashBoardBarChart2 from "../admin/components/DashBoardBarChart2";
import AppSpinner from "../../components/AppSpinner";
import AppTabs from "../admin/components/AppTabs";
import AppSelect from "../admin/components/AppSelect";
import type { TabsProps } from "antd";

interface DashboardStats {
  totalUsers: number;
  todayNewUsers: number;
  totalCounselors: number;
  todayNewCounselors: number;
  totalAICounselingSessions: number;
  todayAICounselingSessions: number;
  totalCounselorSessions: number;
  todayCounselorSessions: number;
  highRiskCounselingCount: number;
}

interface RegistrationStat {
  _id: string;
  count: number;
}

interface CommunityStats {
  posts: { _id: string; count: number }[];
  comments: { _id: string; count: number }[];
}

interface PendingReport {
  _id: string;
  reporter: { displayName: string };
  targetType: "post" | "comment";
  targetId: string;
  reason: string;
  createdAt: string;
  targetInfo: string;
}

const AdminDashboardPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  
  // 차트 데이터 상태
  const [registrationChartData, setRegistrationChartData] = useState<any[]>([]);
  const [communityChartData, setCommunityChartData] = useState<any[]>([]);
  const [pendingReports, setPendingReports] = useState<PendingReport[]>([]);
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // 날짜 범위 텍스트 상태
  const [regDateRange, setRegDateRange] = useState<string>("");
  const [commDateRange, setCommDateRange] = useState<string>("");

  // 필터 값 상태 (현재 선택된 옵션)
  const [regFilter, setRegFilter] = useState<string>("weekly");
  const [commFilter, setCommFilter] = useState<string>("weekly");

  const [activeA, setActiveA] = useState("1");
  const [activeB, setActiveB] = useState("post");

  // --- 날짜 범위 및 리스트 생성 함수 ---
  const generateDateList = (period: string): string[] => {
    const dates: string[] = [];
    const today = new Date();
    let daysToSubtract = 0;

    if (period === 'weekly') daysToSubtract = 6; // 오늘 포함 7일
    else if (period === 'monthly') daysToSubtract = 29; // 오늘 포함 30일
    else if (period === 'yearly') daysToSubtract = 364; // 오늘 포함 365일

    for (let i = 0; i <= daysToSubtract; i++) {
      const d = new Date();
      d.setDate(today.getDate() - (daysToSubtract - i));
      dates.push(d.toISOString().split('T')[0]);
    }
    return dates;
  };

  const calculateDateRange = (period: string): string => {
    const dates = generateDateList(period);
    if (dates.length === 0) return "";
    return `${dates[0]} - ${dates[dates.length - 1]}`;
  };

  // --- 데이터 변환 유틸리티 ---
  // 날짜 형식을 "MM-DD" 형태로 변환
  const formatXAxis = (dateStr: string) => {
    if (!dateStr) return "";
    return dateStr.substring(5); // "2026-02-05" -> "02-05"
  };

  const transformRegistrationData = (data: RegistrationStat[], period: string) => {
    const fullDates = generateDateList(period);
    const dataMap = new Map<string, number>();
    data.forEach(item => dataMap.set(item._id, item.count));

    // 전체 날짜 순회하며 데이터 채우기 (없으면 0)
    return fullDates.map(date => ({
      name: formatXAxis(date),
      value: dataMap.get(date) || 0
    }));
  };

  const transformCommunityData = (data: CommunityStats, period: string) => {
    const { posts, comments } = data;
    const fullDates = generateDateList(period);
    
    // 데이터 조회용 맵 생성
    const postsMap = new Map<string, number>();
    const commentsMap = new Map<string, number>();

    posts.forEach(p => postsMap.set(p._id, p.count));
    comments.forEach(c => commentsMap.set(c._id, c.count));

    // 전체 날짜 순회하며 데이터 채우기
    return fullDates.map(date => ({
      name: formatXAxis(date),
      posts: postsMap.get(date) || 0,
      comments: commentsMap.get(date) || 0
    }));
  };

  // --- API 호출 함수 ---
  const fetchRegistrationStats = async (period: string) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/admin/registration-stats?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // 데이터가 없거나 배열이 아닐 경우 빈 배열로 처리
      const data = Array.isArray(res.data) ? res.data : [];
      setRegistrationChartData(transformRegistrationData(data, period));
      setRegDateRange(calculateDateRange(period));
    } catch (err) {
      console.error("가입자 통계 로드 실패", err);
      // 에러 발생 시에도 빈 그래프를 보여주기 위해 빈 데이터로 변환 호출
      setRegistrationChartData(transformRegistrationData([], period));
      setRegDateRange(calculateDateRange(period));
    }
  };

  const fetchCommunityStats = async (period: string) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/admin/community-stats?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // 데이터 안전 처리
      const data = res.data && typeof res.data === 'object' ? res.data : { posts: [], comments: [] };
      setCommunityChartData(transformCommunityData(data, period));
      setCommDateRange(calculateDateRange(period));
    } catch (err) {
      console.error("커뮤니티 통계 로드 실패", err);
      setCommunityChartData(transformCommunityData({ posts: [], comments: [] }, period));
      setCommDateRange(calculateDateRange(period));
    }
  };


  const handleChange = (name: "donut" | "bar" | "bar2") => async (value: string) => {
    console.log(`[필터 변경] ${name} 선택됨:`, value);
    
    // value 값 매핑 (Select 옵션 value와 실제 period 매핑)
    // 신규 가입자 (bar): Bselect1(주간), Bselect2(월간), Bselect3(연간)
    // 커뮤니티 (bar2): Cselect1(주간), Cselect2(월간), Cselect3(연간)
    
    if (name === "bar") { 
      let period = "weekly";
      if (value === "Bselect2") period = "monthly";
      if (value === "Bselect3") period = "yearly";
      setRegFilter(period);
      await fetchRegistrationStats(period);
    } else if (name === "bar2") { 
      let period = "weekly";
      if (value === "Cselect2") period = "monthly";
      if (value === "Cselect3") period = "yearly";
      setCommFilter(period);
      await fetchCommunityStats(period);
    }
    // donut은 현재 백엔드 로직 부재로 생략
  };



  const itemsA: TabsProps["items"] = [
    { key: "1", label: "게시글 및 댓글", children: (
      <div className="chart-wrap dash-barchart2">
        <DashBoardBarChart2 data={communityChartData} />
      </div>
    ) },
  ];

  const itemsB: TabsProps["items"] = [
    { key: "post", label: "게시글", children: (
      <div className="table-scroll">
        <table className="table user-list-table">
          <colgroup>
            <col style={{ width: 'auto'}} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '20%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>게시글</th>
              <th>신고 사유</th>
              <th>접수 시각</th>
            </tr>
          </thead>
          <tbody>
            {pendingReports.filter(r => r.targetType === 'post').map((report) => (
              <tr key={report._id}>
                <td>
                  <p className="ellipsis-1">{report.targetInfo}</p>
                </td>
                <td>{report.reason}</td>
                <td>{new Date(report.createdAt).toLocaleString()}</td>
              </tr>
            ))}
            {pendingReports.filter(r => r.targetType === 'post').length === 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>신고 내역이 없습니다.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    ), },
    { key: "comment", label: "댓글", children: (
      <div className="table-scroll">
        <table className="table user-list-table">
          <colgroup>
            <col style={{ width: 'auto'}} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '20%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>댓글 내용</th>
              <th>신고 사유</th>
              <th>접수 시각</th>
            </tr>
          </thead>
          <tbody>
            {pendingReports.filter(r => r.targetType === 'comment').map((report) => (
              <tr key={report._id}>
                <td>
                  <p className="ellipsis-1">{report.targetInfo}</p>
                </td>
                <td>{report.reason}</td>
                <td>{new Date(report.createdAt).toLocaleString()}</td>
              </tr>
            ))}
            {pendingReports.filter(r => r.targetType === 'comment').length === 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>신고 내역이 없습니다.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    ) },
  ];

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      setError('로그인이 필요합니다.');
      return;
    }

    const fetchAllData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // 1. 대시보드 기본 통계
        const statsRes = await axios.get('/api/admin/dashboard-stats', { headers });
        setStats(statsRes.data || null);

        // 2. 차트 데이터 (초기값: 주간)
        await Promise.all([
          fetchRegistrationStats('weekly'),
          fetchCommunityStats('weekly')
        ]);

        // 3. 신고 목록
        const reportRes = await axios.get(`/api/admin/pending-reports?targetType=post`, { headers });
        setPendingReports(reportRes.data?.reports || []);

      } catch (err) {
        console.error('데이터 로딩 중 오류 발생:', err);
        setError('데이터를 가져오는 데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [isAuthenticated]);


  useEffect(()=>{

    const reloadReportRes = async ()=>{
        try{
           const token = localStorage.getItem('token');
          const headers = { Authorization: `Bearer ${token}` };
            const reportRes = await axios.get(`/api/admin/pending-reports?targetType=${activeB}`, { headers });
            setPendingReports(reportRes.data?.reports || []);
        }catch(err){
            console.error('데이터 로딩 중 오류 발생:', err);
            // 탭 전환 시 에러가 나더라도 전체 페이지 에러로 띄우기보다는 빈 리스트 유지
            setPendingReports([]); 
        }
      }
    reloadReportRes()
  }, [activeB])

  if (loading) {
    return (
      <AppSpinner fullscreen tip="Loading" />
    );
  }

  if (error) {
    return (
      <div className="admin-page container p-4">
        <p style={{ color: 'red' }}>에러: {error}</p>
      </div>
    );
  }
  

  return (
    <div className="admin-page container p-4 d-flex flex-column gap-24">
      <div className="dash-top gap-24">
        <div className="dash-card col-3 flex-column gap-16">
            <div className="ico"></div>
            <div className="flex-column gap-6">
              <p className="tit">AI 상담 건수</p>
              <div className="flex gap-8 justify-content-between">
                <div className="flex-c gap-8 flex-column-xs align-items-start-xs">
                  <p className="num">{stats?.totalAICounselingSessions || 0}건</p>
                  <p className="txt">고위험 상담 {stats?.highRiskCounselingCount || 0}건</p>
                </div>
                <p className="badge blue"><span className="state"></span>{stats?.todayAICounselingSessions || 0}건</p>
              </div>
            </div>
        </div>
  
        <div className="dash-card col-3 flex-column gap-16">
            <div className="ico"></div>
            <div className="flex-column gap-6">
              <p className="tit">상담사 상담</p>
              <div className="flex gap-8 justify-content-between">
                <div className="flex-c gap-8">
                  <p className="num">{stats?.totalCounselorSessions || 0}건</p>
                </div>
                <p className="badge blue"><span className="state"></span>{stats?.todayCounselorSessions || 0}건</p>
              </div>
            </div>
        </div>
  
        <div className="dash-card col-3 flex-column gap-16">
            <div className="ico"></div>
            <div className="flex-column gap-6">
              <p className="tit">신규 가입자</p>
              <div className="flex gap-8 justify-content-between">
                <div className="flex-c gap-8">
                  <p className="num">{stats?.totalUsers || 0}건</p>
                </div>
                <p className="badge gray"><span className="state"></span>{stats?.todayNewUsers || 0}건</p>
              </div>
            </div>
        </div>
  
        <div className="dash-card col-3 flex-column gap-16">
            <div className="ico"></div>
            <div className="flex-column gap-6">
              <p className="tit">전체 상담사</p>
              <div className="flex gap-8 justify-content-between">
                <div className="flex-c gap-8">
                  <p className="num">{stats?.totalCounselors || 0}건</p>
                </div>
                <p className="badge red"><span className="state"></span>{stats?.todayNewCounselors || 0}건</p>
              </div>
            </div>
        </div>
      </div>
  
      <div className="flex flex-wrap-lg gap-24">
        <div className="col-6 col-lg-12 dash-card">
          <div className="card-hd gap-8 flex-column-sm">
            <h5 className="card-title">상담사 상담 건수</h5>
            <div className="dash-chart-filter w-100-sm flex-column-xs align-items-start-xs">
              <p className="filter-date flex-1-sm">{/* 날짜 범위 - 상담사 데이터 연동 시 구현 */}</p>
              <AppSelect
                defaultValue="Aselect1"
                style={{ width: 120 }}
                onChange={handleChange("donut")}
                options={[
                  { value: "Aselect1", label: "지난 7일" },
                  { value: "Aselect2", label: "지난 30일" },
                  { value: "Aselect3", label: "지난 90일" },
                ]}
              />              
            </div>
          </div>
          <div className="card-body">
            <DashBoardDonutChart />
          </div>
        </div>
  
        <div className="col-6 col-lg-12 dash-card">
          <div className="card-hd gap-8 flex-column-sm align-items-start-xs">
            <h5 className="card-title">신규 가입자 수</h5>
            <div className="dash-chart-filter w-100-sm flex-column-xs align-items-start-xs">
              <p className="filter-date flex-1-sm">{regDateRange}</p>
                  <AppSelect
                    defaultValue="Bselect1"
                    style={{ width: 120 }}
                    onChange={handleChange("bar")}
                    options={[
                      { value: "Bselect1", label: "주간" },
                      { value: "Bselect2", label: "월간" },
                      { value: "Bselect3", label: "연간" },
                    ]}
                  />                  
            </div>
          </div>
          <div className="card-body">
            <div className="chart-wrap">
                <DashBoardBarChart data={registrationChartData} />
            </div> 
          </div>
        </div>
      </div>

      {/* 커뮤니티 운영 현황 / 신고 처리 대기 */}
      <div className="flex flex-wrap-lg gap-24">
        <div className="col-6 col-lg-12 dash-card">
          <div className="card-hd gap-8 flex-column-xs align-items-start-xs">
            <h5 className="card-title">커뮤니티 운영 현황</h5>
          </div>
          <div className="card-body">
            <div className="p-relative flex flex-column-sm align-items-start-sm w-100"> 
              <div className="dash-chart-filter custom w-100-sm gap-8 flex-column-xs align-items-start-xs w-100-xs"
                   style={{ position: 'absolute', right: 0, top: 0, zIndex: 10 }}>
                <p className="filter-date flex-1-sm">{commDateRange}</p>
                <AppSelect
                    defaultValue="Cselect1"
                    style={{ width: 120 }}
                    onChange={handleChange("bar2")}
                    options={[
                      { value: "Cselect1", label: "주간" },
                      { value: "Cselect2", label: "월간" },
                      { value: "Cselect3", label: "연간" },
                    ]}
                  />    
              </div>
              <AppTabs items={itemsA} activeKey={activeA} onChange={setActiveA} className="w-100" />
            </div>
          </div>
        </div>
  
        <div className="col-6 col-lg-12 dash-card">
          <div className="card-hd">
            <h5 className="card-title">신고 처리 대기</h5>
          </div>
          <div className="card-body">
            <AppTabs items={itemsB} activeKey={activeB} onChange={setActiveB} />
          </div>
        </div>
      </div>
    </div>
  );
  
};
export default AdminDashboardPage;